<template>
  <b-modal
    id="modalPathInfo"
    v-model="localIsActive"
    centered
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    header-bg-variant="dark"
    header-text-variant="light"
    @hidden="closeModal"
  >
    <template #modal-title> Path {{ genericDevice.path }} information</template>
    <div class="text-danger" v-if="errorMessage">
      {{ errorMessage }}
    </div>
    <b-row v-if="pathInfo">
      <b-col
        cols="4"
        v-for="(info, index) in pathInfo"
        md="4"
        :key="index">
        <b-form-group
          class="text-capitalize"
          :label="index"
          :label-for="index">
          <b-form-input
            :id="index"
            v-model="pathInfo[index]"
            trim
            :placeholder="index"
            readonly
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="streams">
      <b-col
        cols="4"
        v-for="(info, index) in streams"
        md="6"
        :key="index">
        <b-form-group
          class="text-capitalize"
          :label="index"
          :label-for="index">
          <b-input-group>
            <b-form-input
              :id="index"
              v-model="streams[index]"
              trim
              :placeholder="index"
              v-if="typeof info === 'string'"
              readonly
            />
            <b-input-group-append
              is-text
              class="cursor-pointer"
              @click="copyToClipboard(streams[index])">
              <feather-icon
                color="#000"
                icon="CopyIcon"
              />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="readers && readers.length">
      <div class="readersTitle">Readers</div>
      <b-col
        cols="4"
        v-for="(reader, index) in readers"
        md="6"
        :key="index">
          <div class="readerItem">
            <feather-icon
              color="#000"
              icon="VideoIcon"
              size="20"
            />
            <div class="readerInfoContainer">
              <div class="info">
                <span class="title">{{ reader.type }}</span>
                <div class="id">{{ reader.id }}</div>
              </div>
              <div class="cursor-pointer actions" v-if="reader.type !== 'hlsMuxer'">
                <span
                  class="text"
                  v-if="!reader.isLoading && !reader.isComplete"
                  @click="kickOutSession(reader)">Kick Out</span>
                <b-spinner label="Loading" variant="primary" v-if="reader.isLoading" class="actionIcons"/>
                <feather-icon
                  v-if="reader.isComplete"
                  color="#4BB543"
                  icon="CheckIcon"
                  size="25"
                  class="actionIcons"
                />
              </div>
            </div>
          </div>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from "bootstrap-vue";
import { computed, ref } from "@vue/composition-api";
import axios from "@axios";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {useToast} from "vue-toastification/composition";

export default {
  components: {
    BModal,
    BSpinner,
    vSelect
  },
  props: {
    genericDevice: { type: Object, required: true },
    isModalPathActive: { type: Boolean, required: true },
  },
  setup(props, { emit }) {
    const isModalActive = ref(false);
    const pathInfo = ref(null);
    const streams = ref(null);
    const readers = ref(null);
    const errorMessage = ref('');
    const toast = useToast();

    const localIsActive = computed({
      get() {
        return props.isModalPathActive;
      },
      set(value) {
        emit("update:isActive", value);
      },
    });

    const closeModal = () => {
      emit('close-modal');
    };

    const getPathInfo = async () => {
      try {
        const params = {
          id_proxy_rtsp_server: props.genericDevice.id_proxy_rtsp_server,
          path: props.genericDevice.path,
        }
        const {data} = await axios.get(`/v1/generic-device/get-path-info`, {params});
  
        if (data.success) {
          pathInfo.value = data.data.pathInfo
          streams.value = data.data.streams
          setReadersInfo(data.data.readers)
        }
      } catch {
        errorMessage.value = "Error getting path information, try again later, if the error persists, contact the administrator."
        toast({
        component: ToastificationContent,
        props: {
          title: "Error getting path information",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
      }
    };
    getPathInfo()

    const copyToClipboard = (streamUrl) => {
      navigator.clipboard.writeText(streamUrl);
      toast({
        component: ToastificationContent,
        props: {
          title: "Copiado correctamente",
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    const setReadersInfo = (allReaders) => {
      const updatedReaders = allReaders.map(obj => ({
        ...obj,
        isLoading: false,
        isComplete: false,
        hasErrors: false
      }));
      readers.value = updatedReaders
    };

    const kickOutSession = async (session) => {
      try {
        const params = {
          id_proxy_rtsp_server: props.genericDevice.id_proxy_rtsp_server,
          streamType: session.type,
          sessionId: session.id,
        }
        session.isLoading = true
        await axios.post(`/v1/generic-device/kick-out-session`, params);
        session.isLoading = false
        session.isComplete = true
        setTimeout(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: "Kick out session success",
              icon: "CheckIcon",
              variant: "success",
            },
          });
          removeSessionFromReader(session.id)
        }, 500);
      } catch {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error on kick out session",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    };

    const removeSessionFromReader = (sessionId) => {
      readers.value = readers.value.filter(obj => obj.id !== sessionId);
    };

    return {
      closeModal,
      isModalActive,
      localIsActive,
      pathInfo,
      streams,
      copyToClipboard,
      errorMessage,
      readers,
      kickOutSession
    };
  },
};
</script>

<style scoped>
.readerItem {
  width: 100%;
  display: flex;
  margin: 5px;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  /* justify-content: center; */
  align-items: center;
  padding: 5px 10px;
}
.readerInfoContainer {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 10px;
}
.readerInfoContainer > .info > .title{
  font-size: 14px;
  font-weight: bolder;
  color: #000;
}
.readerInfoContainer > .info > .id{
  font-size: 12px;
}
.readerInfoContainer > .actions{
  margin-left: 25px;
  padding-left: 12px;
}
.readerInfoContainer > .actions > .text{
  color: #2266ee;
}
.readersTitle {
  width: 100%;
  margin-left: 15px;
}
</style>
